import { FC, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { UiAiTransform } from '../../provider/UiContext';

interface ITextareaAiAssistProps {
    /** Ключ AI-улучшения поля (aiAutocompleteKey). */
    aiKey: string;
    /** Запрос улучшения — `aiTransform` из `UiProvider`. */
    transform: UiAiTransform;
    /** Текущее содержимое поля. */
    getText: () => string;
    /** Применить улучшенный текст к полю и форме. */
    onResult: (text: string) => void;
    /** Сообщить наружу о начале/конце улучшения (для слоя свечения позади поля). */
    onBusyChange?: (busy: boolean) => void;
}

/** Защита от зависшего бэкенда. */
const IMPROVE_TIMEOUT_MS = 20000;

/** Белая искра на градиентной кнопке (силуэт AISparklesAnimated): цвет наследует от кнопки. */
const SparkIcon: FC = () => (
    <svg className="rf-ai-btn__spark" width={15} height={15} viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
        <path d="M16 6.5 17.6 14.4 25.5 16 17.6 17.6 16 25.5 14.4 17.6 6.5 16 14.4 14.4Z" />
        <path d="M7 2 7.7 5.3 11 6 7.7 6.7 7 10 6.3 6.7 3 6 6.3 5.3Z" />
        <path d="M25 22 25.5 24.5 28 25 25.5 25.5 25 28 24.5 25.5 22 25 24.5 24.5Z" />
    </svg>
);

/**
 * AI-улучшение текста на поле «Многострочный текст», вариант «Созвездие»:
 * по клику поле получает анимированную AI-обводку, и улучшенный текст
 * проявляется по словам. Кнопка остаётся активной — текст можно улучшать
 * итеративно. Рендерится внутри `.rf-textarea__wrapper--ai` (position: relative).
 */
const TextareaAiAssist: FC<ITextareaAiAssistProps> = ({ aiKey, transform, getText, onResult, onBusyChange }) => {
    const revealRef = useRef<HTMLDivElement>(null);
    const busyRef = useRef(false);
    const mountedRef = useRef(true);
    const timersRef = useRef<Set<number>>(new Set());
    const abortRef = useRef<AbortController | null>(null);

    const [busy, setBusy] = useState(false);

    const reduce =
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
            : false;

    useEffect(
        () => () => {
            mountedRef.current = false;
            abortRef.current?.abort();
            timersRef.current.forEach((id) => clearTimeout(id));
            timersRef.current.clear();
        },
        [],
    );

    // Прокидываем busy наружу — слой свечения живёт на уровне .rf-textarea
    useEffect(() => {
        onBusyChange?.(busy);
    }, [busy, onBusyChange]);

    const wait = useCallback(
        (ms: number) =>
            new Promise<void>((res) => {
                const id = window.setTimeout(() => {
                    timersRef.current.delete(id);
                    res();
                }, ms);
                timersRef.current.add(id);
            }),
        [],
    );

    /** Проявление по словам поверх поля; apply() подменяет значение под оверлеем. */
    const reveal = useCallback(
        async (text: string, apply: () => void) => {
            const ov = revealRef.current;
            if (!ov || reduce) {
                apply();
                return;
            }
            // Типографику и паддинги копируем с живого поля: Mantine-textarea меняет их по
            // size/контексту (--input-fz в формах), и статический SCSS-паритет разъезжается.
            const field = ov.closest('.rf-textarea__ai-host')?.querySelector('textarea');
            if (field) {
                const cs = window.getComputedStyle(field);
                ov.style.fontFamily = cs.fontFamily;
                ov.style.fontSize = cs.fontSize;
                ov.style.lineHeight = cs.lineHeight;
                ov.style.fontWeight = cs.fontWeight;
                ov.style.letterSpacing = cs.letterSpacing;
                ov.style.padding = cs.padding;
                ov.style.borderRadius = cs.borderRadius;
            }
            ov.replaceChildren();
            const spans: HTMLSpanElement[] = [];
            text.split(/(\s+)/).forEach((tok) => {
                if (!tok.trim()) {
                    ov.appendChild(document.createTextNode(tok));
                    return;
                }
                const s = document.createElement('span');
                s.className = 'rf-ai-w';
                s.textContent = tok;
                ov.appendChild(s);
                spans.push(s);
            });
            spans.forEach((s, i) => {
                s.style.animationDelay = `${i * 28}ms`;
            });
            ov.style.transition = 'none';
            ov.style.visibility = 'visible';
            ov.style.opacity = '1';
            void ov.offsetWidth;
            apply(); // значение подменяется под непрозрачным оверлеем — без «мигания»
            ov.classList.add('is-on');

            await wait(spans.length * 28 + 420);
            ov.style.transition = 'opacity .22s ease';
            ov.style.opacity = '0';
            await wait(240);
            ov.style.visibility = 'hidden';
            ov.classList.remove('is-on');
            ov.replaceChildren();
        },
        [reduce, wait],
    );

    const run = useCallback(async () => {
        if (busyRef.current) return;
        const src = getText();
        busyRef.current = true;
        setBusy(true);
        const minThink = reduce ? 240 : 720;
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const timeout = new Promise<never>((_, reject) => {
                const id = window.setTimeout(() => {
                    timersRef.current.delete(id);
                    controller.abort();
                    reject(new Error('improve timeout'));
                }, IMPROVE_TIMEOUT_MS);
                timersRef.current.add(id);
            });
            const [text] = await Promise.all([
                Promise.race([transform({ key: aiKey, text: src, signal: controller.signal }), timeout]),
                wait(minThink),
            ]);
            if (!mountedRef.current) return;
            await reveal(text, () => onResult(text));
        } catch {
            // тихо: поле остаётся с прежним текстом, кнопка снова активна
        } finally {
            if (abortRef.current === controller) abortRef.current = null;
            busyRef.current = false;
            if (mountedRef.current) setBusy(false);
        }
    }, [aiKey, transform, getText, onResult, reduce, reveal, wait]);

    return (
        <>
            <div className={clsx('rf-ai-ring', busy && 'is-on')} aria-hidden="true" />
            <div className="rf-ai-reveal" ref={revealRef} aria-hidden="true" />
            <button
                type="button"
                className={clsx('rf-ai-btn', busy && 'is-busy')}
                onClick={() => void run()}
                disabled={busy}
                title="Улучшить текст с помощью AI"
                aria-label="Улучшить текст с помощью AI"
                aria-busy={busy}
            >
                <SparkIcon />
                <span className="rf-ai-btn__label">{busy ? 'Улучшаю…' : 'Улучшить'}</span>
            </button>
        </>
    );
};

export default TextareaAiAssist;
